import * as React from 'react';
import Link from 'next/link';
import { Trans, useTranslation } from 'react-i18next';
import { Container } from '../../../components/web/container/container';
import { Headline } from '../../../components/app/headline/headline';
import styles from './homepage-process.module.css';

function ExportGuideLink({ children }: React.PropsWithChildren) {
    return (
        <Link href="https://monery.zendesk.com/hc/cs/sections/29109217807377-Export-dat">
            <a className="text-brand-secondary underline decoration-border underline-offset-4 transition-colors hover:text-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary">
                {children}
            </a>
        </Link>
    );
}

export function IllustrationStage({ children }: { children: React.ReactNode }) {
    return (
        <div aria-hidden="true" className="relative flex h-[20rem] w-[78%] items-end justify-start pb-[1rem] pr-small">
            {children}
        </div>
    );
}

function ExportIllustration() {
    return (
        <IllustrationStage>
            <div className="relative ml-[0.7rem] flex w-full max-w-[24rem] items-center justify-center">
                <div
                    className={`${styles.glassPanel} absolute left-0 top-[1rem] h-[15rem] w-[13.5rem] -rotate-6 rounded-medium border border-[#a99c91]/45`}
                >
                    <div className="flex h-[2.6rem] items-center gap-[0.4rem] border-b border-[#a99c91]/45 px-small">
                        <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/30" />
                        <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/20" />
                        <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/10" />
                    </div>
                    <div className={`${styles.exportRows} space-y-[0.8rem] px-small pt-small`}>
                        <div className="h-[0.6rem] w-3/4 rounded-full bg-brand-primary/15" />
                        <div className="h-[0.6rem] w-full rounded-full bg-brand-primary/10" />
                        <div className="h-[0.6rem] w-5/6 rounded-full bg-brand-primary/10" />
                    </div>
                </div>
                <div
                    className={`${styles.glassPanel} ${styles.exportFile} relative ml-auto mt-[1.8rem] h-[15rem] w-[12.5rem] rotate-6 rounded-medium border border-[#a99c91]/45 p-small`}
                >
                    <div className="mb-small flex items-center justify-between">
                        <div className="h-[2.4rem] w-[2.4rem] rounded-small bg-brand-primary/15" />
                        <div className="rounded-small bg-brand-secondary/10 px-[0.7rem] py-[0.3rem] text-[1rem] font-bold tracking-[0.08em] text-brand-secondary">
                            CSV
                        </div>
                    </div>
                    <div className={`${styles.exportRows} space-y-[0.7rem]`}>
                        <div className="h-[0.5rem] w-full rounded-full bg-brand-primary/15" />
                        <div className="h-[0.5rem] w-4/5 rounded-full bg-brand-primary/10" />
                    </div>
                </div>
            </div>
        </IllustrationStage>
    );
}

function UploadIllustration() {
    return (
        <IllustrationStage>
            <div
                className={`${styles.glassPanel} relative w-full max-w-[22rem] rounded-large border border-[#a99c91]/45 p-[0.8rem]`}
            >
                <div className="mb-[0.8rem] flex h-[2.2rem] items-center gap-[0.4rem] border-b border-[#a99c91]/45 px-[0.6rem]">
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/30" />
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/20" />
                    <span className="ml-auto h-[0.5rem] w-[4rem] rounded-full bg-brand-primary/15" />
                </div>
                <div className="flex h-[11.5rem] flex-col items-center justify-center rounded-medium border border-dashed border-[#a99c91]/45">
                    <div className="mb-[0.7rem] flex h-[3.7rem] w-[3.7rem] items-center justify-center rounded-medium bg-brand-primary text-[1.8rem] font-bold leading-none text-text-inverse">
                        ↑
                    </div>
                    <div className="h-[0.6rem] w-[8rem] overflow-hidden rounded-full bg-brand-primary/10">
                        <div className={`${styles.uploadProgress} h-full rounded-full bg-brand-primary/60`} />
                    </div>
                </div>
                <div
                    className={`${styles.glassPanel} absolute -bottom-[1rem] -right-[1rem] flex items-center gap-[0.7rem] rounded-medium border border-[#a99c91]/45 px-small py-[0.7rem]`}
                >
                    <span className="rounded-small bg-brand-secondary/10 px-[0.5rem] py-[0.3rem] text-[0.9rem] font-bold tracking-[0.08em] text-brand-secondary">
                        CSV
                    </span>
                    <span className="h-[0.5rem] w-[4.5rem] overflow-hidden rounded-full bg-brand-secondary/10">
                        <span className={`${styles.csvProgress} block h-full rounded-full bg-brand-secondary/60`} />
                    </span>
                    <span className="h-[0.6rem] w-[0.6rem] rounded-full bg-brand-secondary" />
                </div>
            </div>
        </IllustrationStage>
    );
}

export function AffiliateAccountIllustration() {
    return (
        <IllustrationStage>
            <div
                className={`${styles.glassPanel} relative w-full max-w-[22rem] rounded-large border border-[#a99c91]/45 p-[0.8rem]`}
            >
                <div className="mb-[0.8rem] flex h-[2rem] items-center gap-[0.4rem] border-b border-[#a99c91]/45 px-[0.6rem]">
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/30" />
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/20" />
                    <span className="ml-auto h-[0.5rem] w-[4rem] rounded-full bg-brand-primary/15" />
                </div>
                <div className="flex items-center gap-[0.8rem] rounded-medium border border-[#a99c91]/25 p-[0.8rem]">
                    <span className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-full bg-brand-primary/15">
                        <span className="h-[1.8rem] w-[1.8rem] rounded-full border-[0.4rem] border-brand-primary/55" />
                    </span>
                    <span className="flex-1 space-y-[0.7rem]">
                        <span className="block h-[0.6rem] w-3/4 rounded-full bg-brand-secondary/55" />
                        <span className="block h-[0.5rem] w-1/2 rounded-full bg-brand-primary/20" />
                    </span>
                </div>
                <div className="mt-[0.8rem] flex items-center justify-between rounded-medium bg-brand-secondary/5 px-[0.8rem] py-[0.7rem]">
                    <span className="space-y-[0.5rem]">
                        <span className="block h-[0.4rem] w-[5rem] rounded-full bg-brand-secondary/25" />
                        <span className="block h-[0.6rem] w-[8rem] rounded-full bg-brand-secondary/55" />
                    </span>
                    <span
                        className={`${styles.accountConfirmation} flex h-[2.8rem] w-[2.8rem] items-center justify-center rounded-full bg-brand-primary text-[1.5rem] font-bold text-text-inverse`}
                    >
                        ✓
                    </span>
                </div>
            </div>
        </IllustrationStage>
    );
}

export function AffiliateReferralIllustration() {
    return (
        <IllustrationStage>
            <div
                className={`${styles.glassPanel} relative w-full max-w-[23rem] rounded-large border border-[#a99c91]/45 p-[0.8rem]`}
            >
                <div className="mb-[0.8rem] flex h-[2rem] items-center gap-[0.4rem] border-b border-[#a99c91]/45 px-[0.6rem]">
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/30" />
                    <span className="h-[0.5rem] w-[0.5rem] rounded-full bg-brand-primary/20" />
                    <span className="ml-auto h-[0.5rem] w-[4rem] rounded-full bg-brand-primary/15" />
                </div>
                <div className="rounded-medium border border-[#a99c91]/25 p-[0.8rem]">
                    <div className="mb-[0.8rem] h-[0.6rem] w-[7rem] rounded-full bg-brand-secondary/25" />
                    <div className="flex items-center gap-[0.7rem] rounded-medium bg-brand-primary/10 px-[0.8rem] py-[0.8rem]">
                        <span className="min-w-0 flex-1 truncate text-[1.05rem] font-semibold text-brand-secondary">
                            monery.io/a/vas-kod
                        </span>
                        <span
                            className={`${styles.referralCopy} flex h-[2.6rem] w-[2.6rem] shrink-0 items-center justify-center rounded-small bg-brand-primary text-[1.3rem] font-bold text-text-inverse`}
                        >
                            ↗
                        </span>
                    </div>
                </div>
                <div className="mt-[0.8rem] flex items-center justify-center gap-[1rem]">
                    {[0, 1, 2].map((item) => (
                        <React.Fragment key={item}>
                            {item > 0 && <span className="h-px w-[2.2rem] bg-[#a99c91]/40" />}
                            <span
                                className={`${styles.referralNode} h-[2.4rem] w-[2.4rem] rounded-full border border-[#a99c91]/40 bg-[#fdf7f2]`}
                            />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </IllustrationStage>
    );
}

function DashboardGrid() {
    return (
        <div className={`${styles.dashboardGrid} absolute inset-0 grid grid-cols-[1.2fr_1fr] grid-rows-2 gap-[0.5rem]`}>
            <div className="row-span-2 flex items-end justify-center gap-[0.4rem] rounded-[0.5rem] border border-[#a99c91]/25 p-[0.7rem]">
                {[35, 60, 48, 85, 72].map((height) => (
                    <span
                        key={height}
                        className="w-[0.7rem] rounded-t-[0.2rem] bg-brand-primary/50"
                        style={{ height: `${height}%` }}
                    />
                ))}
            </div>
            <div className="flex items-center gap-[0.5rem] rounded-[0.5rem] border border-[#a99c91]/25 px-[0.5rem]">
                <span className="h-[1.5rem] w-[1.5rem] shrink-0 rounded-full border-[0.4rem] border-brand-primary/40 border-r-brand-secondary/60" />
                <span className="h-[0.4rem] w-[2rem] rounded-full bg-brand-secondary/30" />
            </div>
            <div className="flex flex-col justify-center gap-[0.4rem] rounded-[0.5rem] border border-[#a99c91]/25 px-[0.6rem]">
                <span className="h-[0.3rem] w-4/5 rounded-full bg-brand-secondary/40" />
                <span className="h-[0.3rem] w-3/5 rounded-full bg-brand-primary/40" />
            </div>
        </div>
    );
}

export function PortfolioIllustration() {
    return (
        <IllustrationStage>
            <div
                className={`${styles.glassPanel} relative w-full max-w-[24rem] rounded-large border border-[#a99c91]/45 p-small pl-[4.6rem]`}
            >
                <div className="absolute inset-y-0 left-0 flex w-[3rem] flex-col items-center gap-[1.1rem] rounded-l-large border-r border-[#a99c91]/20 bg-brand-secondary/5 pt-[1.8rem]">
                    <span className="mb-[0.6rem] h-[1.2rem] w-[1.2rem] rounded-[0.4rem] bg-brand-primary" />
                    <span className="h-[0.4rem] w-[1.4rem] rounded-full bg-brand-secondary/60" />
                    <span className="h-[0.4rem] w-[1.4rem] rounded-full bg-brand-secondary/20" />
                    <span className="h-[0.4rem] w-[1.4rem] rounded-full bg-brand-secondary/20" />
                </div>
                <div className="mb-small flex items-center justify-between">
                    <div className="space-y-[0.6rem]">
                        <div className="h-[0.5rem] w-[5rem] rounded-full bg-brand-primary/15" />
                        <div className="h-[0.9rem] w-[8rem] rounded-full bg-brand-secondary/70" />
                    </div>
                    <div className="flex gap-[0.3rem]">
                        <span className={`${styles.chartTab} h-[0.5rem] w-[1.2rem] rounded-full bg-brand-primary/60`} />
                        <span className={`${styles.gridTab} h-[0.5rem] w-[1.2rem] rounded-full bg-brand-primary/60`} />
                    </div>
                </div>
                <div className="relative h-[6.4rem]">
                    <svg
                        viewBox="0 0 220 78"
                        className={`${styles.chartView} h-[6.4rem] w-full`}
                        preserveAspectRatio="none"
                        focusable="false"
                    >
                        <defs>
                            <linearGradient
                                id="homepage-process-chart-fill"
                                x1="0"
                                y1="8"
                                x2="0"
                                y2="78"
                                gradientUnits="userSpaceOnUse"
                                className="text-brand-primary"
                            >
                                <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
                                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        <path
                            d="M4 24 H216 M4 48 H216"
                            fill="none"
                            stroke="currentColor"
                            strokeDasharray="3 7"
                            className="text-web-copy/20"
                        />
                        <path
                            d="M4 66 C26 62 34 48 56 53 S86 30 108 36 S142 24 160 28 S190 12 216 8 V78 H4 Z"
                            fill="url(#homepage-process-chart-fill)"
                            className={styles.chartArea}
                        />
                        <path
                            d="M4 66 C26 62 34 48 56 53 S86 30 108 36 S142 24 160 28 S190 12 216 8"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            pathLength="1"
                            className={`${styles.chartLine} text-brand-primary`}
                        />
                    </svg>
                    <DashboardGrid />
                </div>
                <div className="mt-[1.6rem] flex items-center gap-[0.6rem]">
                    <div className="h-[0.5rem] w-1/2 rounded-full bg-brand-primary" />
                    <div className="h-[0.5rem] w-1/3 rounded-full bg-brand-secondary/70" />
                    <div className="h-[0.5rem] flex-1 rounded-full bg-brand-primary/25" />
                </div>
            </div>
        </IllustrationStage>
    );
}

export interface WebProcessStep {
    id: string;
    title: React.ReactNode;
    description: React.ReactNode;
    illustration: React.ReactNode;
}

interface WebProcessProps {
    title: React.ReactNode;
    description?: React.ReactNode;
    steps: WebProcessStep[];
    footer?: React.ReactNode;
}

const numberOffsets = ['21rem', '19rem', '20rem'];

export const WebProcess: React.FC<WebProcessProps> = ({ title, description, steps, footer }) => {
    const sectionRef = React.useRef<HTMLElement>(null);
    const [running, setRunning] = React.useState(false);

    React.useEffect(() => {
        const section = sectionRef.current;
        if (!section) return undefined;
        const observer = new IntersectionObserver(([entry]) => setRunning(entry.isIntersecting), { threshold: 0.15 });
        observer.observe(section);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="jak-to-funguje"
            data-running={running}
            className={`${styles.process} relative isolate overflow-hidden py-xlarge scroll-mt-large xxl:py-[12rem]`}
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -left-[78rem] bottom-[-14rem] -z-[1] hidden h-[85rem] w-[145rem] -rotate-[18deg] -scale-x-100 xxl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-cover bg-center opacity-[0.22] [mask-image:radial-gradient(ellipse_at_center,black,transparent_68%)] xxl:block"
            />
            <Container>
                <div className="mx-auto mb-[5rem] max-w-[80rem] text-center">
                    <Headline level="1" tag="h2" align="center">
                        {title}
                    </Headline>
                    {description && (
                        <p className="mx-auto mt-[2rem] max-w-[68rem] text-[1.7rem] leading-relaxed text-web-copy">
                            {description}
                        </p>
                    )}
                </div>
                <ol className="grid list-none gap-[4.8rem] vl:grid-cols-3 vl:gap-[4rem]">
                    {steps.map((step, index) => (
                        <li key={step.id} className="relative mx-auto w-full max-w-[40rem]">
                            <div className="relative isolate">
                                <span
                                    aria-hidden="true"
                                    style={{
                                        left: numberOffsets[index],
                                        animationDelay: `${index === 0 ? 0 : (index - 3) * 3}s`,
                                    }}
                                    className={`${styles.stepNumber} pointer-events-none absolute bottom-[-1rem] -z-[1] select-none bg-gradient-to-b from-brand-secondary/20 via-brand-primary/10 to-transparent bg-clip-text pr-[0.12em] text-[12rem] font-bold leading-none tracking-[-0.04em] text-transparent`}
                                >
                                    {String(index + 1).padStart(2, '0')}
                                </span>
                                {step.illustration}
                            </div>
                            <div className="relative mt-[2.4rem] max-w-[34rem]">
                                <h3 className="mb-small text-[2.2rem] font-bold text-brand-secondary leading-[1.3]">
                                    {step.title}
                                </h3>
                                <p className="text-[1.6rem] leading-[2.6rem] text-web-copy">{step.description}</p>
                            </div>
                        </li>
                    ))}
                </ol>
                {footer && <div className="mt-[3.2rem] vl:mt-[4rem]">{footer}</div>}
            </Container>
        </section>
    );
};

export const HomepageProcess: React.FC = () => {
    const { t } = useTranslation();
    const steps: WebProcessStep[] = [
        {
            id: 'export',
            title: t('pages.homepage.process.step1.title'),
            description: (
                <Trans
                    i18nKey="pages.homepage.process.step1.description"
                    components={{
                        guide: <ExportGuideLink />,
                    }}
                />
            ),
            illustration: <ExportIllustration />,
        },
        {
            id: 'upload',
            title: t('pages.homepage.process.step2.title'),
            description: t('pages.homepage.process.step2.description'),
            illustration: <UploadIllustration />,
        },
        {
            id: 'portfolio',
            title: t('pages.homepage.process.step3.title'),
            description: t('pages.homepage.process.step3.description'),
            illustration: <PortfolioIllustration />,
        },
    ];

    return (
        <WebProcess
            title={t('pages.homepage.process.title')}
            steps={steps}
            footer={
                <>
                    <div aria-hidden="true" className="relative mx-auto mb-[2rem] h-[2.8rem] w-full vl:w-2/3">
                        <div className="absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-[#a99c91]/10 via-[#a99c91]/40 to-[#a99c91]/10 vl:block" />
                        <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[#a99c91]/40" />
                        <div className="absolute bottom-[0.1rem] left-1/2 h-[0.7rem] w-[0.7rem] -translate-x-1/2 rotate-45 border-b border-r border-[#a99c91]/40" />
                    </div>
                    <p className="flex flex-col items-center justify-center gap-[0.4rem] text-center text-[2rem] font-bold leading-[3rem] text-web-copy vl:flex-row vl:gap-[1.2rem] vl:text-[2.4rem] vl:leading-[3.6rem]">
                        <span className="text-web-copy">{t('pages.homepage.process.brokerNoteBroker')}</span>
                        <span>
                            <Trans
                                i18nKey="pages.homepage.process.brokerNoteOverview"
                                components={{ brand: <span className="text-brand-primary" /> }}
                            />
                        </span>
                    </p>
                </>
            }
        />
    );
};
